import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  describeMessagePreview,
  MAX_BODY_LENGTH,
  MAX_IMAGE_FILE_BYTES,
  normalizeLoginName,
} from "./chatService";
import EmojiPickerPopover from "./EmojiPickerPopover";

const SUBMIT_TIMEOUT_MS = 12000;

function withSubmitTimeout(promise, timeoutMs, message) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

function formatFileSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function getMentionDraft(value, caret) {
  if (typeof caret !== "number") return null;
  const beforeCaret = value.slice(0, caret);
  const match = beforeCaret.match(/(^|\s)@([a-z0-9]*)$/i);
  if (!match) return null;
  return {
    start: beforeCaret.lastIndexOf("@"),
    end: caret,
    query: match[2] || "",
  };
}

export default function MessageInput({
  onSend,
  onTyping,
  disabled,
  placeholder,
  replyTo,
  onCancelReply,
  memberIds = [],
  meUid = "",
  editingMessage = null,
  onCancelEdit,
  profilesByUid = {},
}) {
  const [text, setText] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mentionDraft, setMentionDraft] = useState(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const taRef = useRef(null);
  const fileInputRef = useRef(null);
  const previousEditingIdRef = useRef(null);
  const dragDepthRef = useRef(0);

  const mentionCandidates = useMemo(() => {
    const memberCandidates = memberIds
      .filter((uid) => uid && uid !== meUid)
      .map((uid) => profilesByUid[uid])
      .filter(Boolean)
      .map((profile) => {
        const mentionHandle = normalizeLoginName(profile.loginName || profile.displayName || "");
        return mentionHandle ? { ...profile, mentionHandle } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || ""));
    return [
      { uid: "__everyone__", displayName: "Everyone", mentionHandle: "everyone" },
      { uid: "__here__", displayName: "Here", mentionHandle: "here" },
      ...memberCandidates,
    ];
  }, [memberIds, meUid, profilesByUid]);

  const filteredMentions = useMemo(() => {
    if (!mentionDraft) return [];
    const query = normalizeLoginName(mentionDraft.query || "");
    return mentionCandidates
      .filter((profile) => {
        if (!query) return true;
        const displayName = normalizeLoginName(profile.displayName || "");
        return profile.mentionHandle.includes(query) || displayName.includes(query);
      })
      .slice(0, 8);
  }, [mentionCandidates, mentionDraft]);
  const mentionQuery = mentionDraft ? mentionDraft.query : "";

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(160, ta.scrollHeight) + "px";
  }, [text]);

  useEffect(() => {
    setActiveMentionIndex(0);
  }, [mentionQuery]);

  const clearSelectedImage = useCallback(() => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePreviewUrl("");
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [imagePreviewUrl]);

  useEffect(() => {
    if (editingMessage && editingMessage.id !== previousEditingIdRef.current) {
      const nextText = editingMessage.body || "";
      previousEditingIdRef.current = editingMessage.id;
      setText(nextText);
      setMentionDraft(null);
      clearSelectedImage();
      requestAnimationFrame(() => {
        if (!taRef.current) return;
        taRef.current.focus();
        const caret = nextText.length;
        taRef.current.setSelectionRange(caret, caret);
      });
      return;
    }
    if (!editingMessage && previousEditingIdRef.current) {
      previousEditingIdRef.current = null;
      setText("");
      setMentionDraft(null);
    }
  }, [clearSelectedImage, editingMessage]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  function syncMentionDraft(nextText, caret) {
    setMentionDraft(getMentionDraft(nextText, caret));
  }

  async function submit() {
    const body = text.trim();
    if (!body && !imageFile) return;
    setIsSubmitting(true);
    onTyping && onTyping(false);
    // Optimistic clear: free the composer immediately so typing the next message
    // feels instant even while the network/encryption work for a large group is
    // still in flight. We restore on failure.
    const previousText = text;
    const previousImageFile = imageFile;
    const previousImagePreviewUrl = imagePreviewUrl;
    setText("");
    setMentionDraft(null);
    setImagePreviewUrl("");
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    try {
      await withSubmitTimeout(
        onSend({
          body,
          replyTo,
          imageFile: previousImageFile,
        }),
        SUBMIT_TIMEOUT_MS,
        "Send is taking too long. Firestore may be throttled right now; please try again.",
      );
      if (previousImagePreviewUrl) URL.revokeObjectURL(previousImagePreviewUrl);
      requestAnimationFrame(() => {
        if (!taRef.current) return;
        taRef.current.focus();
        taRef.current.setSelectionRange(0, 0);
      });
    } catch (e) {
      // Restore the composer so the user doesn't lose their draft.
      setText(previousText);
      setImageFile(previousImageFile);
      setImagePreviewUrl(previousImagePreviewUrl);
      alert(
        "Couldn't send" +
          (e && e.code ? ` (${e.code})` : "") +
          ": " +
          (e && e.message ? e.message : "unknown error")
      );
      requestAnimationFrame(() => {
        if (!taRef.current) return;
        taRef.current.focus();
        taRef.current.setSelectionRange(previousText.length, previousText.length);
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function changeText(next, caret = next.length) {
    setText(next);
    onTyping && onTyping(!!next.trim());
    syncMentionDraft(next, caret);
  }

  function addEmoji(emoji) {
    setText((prev) => {
      const next = prev + emoji;
      onTyping && onTyping(!!next.trim());
      return next;
    });
    requestAnimationFrame(() => taRef.current?.focus());
  }

  function openFilePicker() {
    if (composerDisabled || editingMessage) return;
    fileInputRef.current?.click();
  }

  const acceptImageFile = useCallback((file) => {
    if (!file) return false;
    if (!String(file.type || "").toLowerCase().startsWith("image/")) {
      clearSelectedImage();
      alert("Only image files are allowed.");
      return false;
    }
    if (file.size > MAX_IMAGE_FILE_BYTES) {
      clearSelectedImage();
      alert("Images must be 350 KB or smaller.");
      return false;
    }
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
    requestAnimationFrame(() => taRef.current?.focus());
    return true;
  }, [clearSelectedImage, imagePreviewUrl]);

  function extractImageFile(dataTransfer) {
    if (!dataTransfer) return null;
    const items = Array.from(dataTransfer.items || []);
    for (const item of items) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) return file;
      }
    }
    const files = Array.from(dataTransfer.files || []);
    return files[0] || null;
  }

  function hasFileTransfer(dataTransfer) {
    if (!dataTransfer) return false;
    const items = Array.from(dataTransfer.items || []);
    if (items.some((item) => item.kind === "file")) return true;
    return Array.from(dataTransfer.files || []).length > 0;
  }

  function onPickImage(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    acceptImageFile(file);
  }

  function onPaste(e) {
    if (composerDisabled || editingMessage) return;
    const file = extractImageFile(e.clipboardData);
    if (!file) return;
    e.preventDefault();
    acceptImageFile(file);
  }

  function onDragEnter(e) {
    if (composerDisabled || editingMessage || !hasFileTransfer(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  }

  function onDragOver(e) {
    if (composerDisabled || editingMessage || !hasFileTransfer(e.dataTransfer)) return;
    e.preventDefault();
    if (!isDragActive) setIsDragActive(true);
  }

  function onDragLeave(e) {
    if (composerDisabled || editingMessage || !hasFileTransfer(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragActive(false);
  }

  function onDrop(e) {
    if (composerDisabled || editingMessage || !hasFileTransfer(e.dataTransfer)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    const file = extractImageFile(e.dataTransfer);
    if (!file) return;
    acceptImageFile(file);
  }

  function onKeyDown(e) {
    if (mentionDraft && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveMentionIndex((cur) => (cur + 1) % filteredMentions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveMentionIndex((cur) => (cur - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMentions[activeMentionIndex]);
        return;
      }
    }
    if (mentionDraft && e.key === "Escape") {
      e.preventDefault();
      setMentionDraft(null);
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  function insertMention(profile) {
    if (!profile || !mentionDraft) return;
    const before = text.slice(0, mentionDraft.start);
    const after = text.slice(mentionDraft.end);
    const mentionText = `@${profile.mentionHandle} `;
    const nextText = before + mentionText + after;
    const caret = before.length + mentionText.length;
    setText(nextText);
    onTyping && onTyping(true);
    setMentionDraft(null);
    setActiveMentionIndex(0);
    requestAnimationFrame(() => {
      if (!taRef.current) return;
      taRef.current.focus();
      taRef.current.setSelectionRange(caret, caret);
    });
  }

  function handleSelectionChange() {
    if (!taRef.current) return;
    syncMentionDraft(text, taRef.current.selectionStart);
  }

  const replyPreview = replyTo ? describeMessagePreview(replyTo) || "Message" : "";
  const composerDisabled = !!disabled || isSubmitting;
  const canSend = !composerDisabled && (!!text.trim() || !!imageFile);

  return (
    <div className="chat-msg-input">
      {editingMessage && (
        <div className="chat-edit-compose">
          <div>
            <span>Editing message</span>
            <p>{describeMessagePreview(editingMessage) || "Message"}</p>
          </div>
          <button type="button" className="chat-btn-icon" onClick={onCancelEdit} title="Cancel edit">✕</button>
        </div>
      )}
      {replyTo && (
        <div className="chat-reply-compose">
          <div>
            <span>Replying to {profilesByUid[replyTo.senderId]?.displayName || "message"}</span>
            <p>{replyPreview}</p>
          </div>
          <button type="button" className="chat-btn-icon" onClick={onCancelReply} title="Cancel reply">✕</button>
        </div>
      )}
      {imagePreviewUrl && !editingMessage && (
        <div className="chat-image-compose">
          <div className="chat-image-compose-thumb">
            <img src={imagePreviewUrl} alt="Selected upload preview" />
          </div>
          <div className="chat-image-compose-meta">
            <span>Ready to send image</span>
            <p>{imageFile?.name || "Image"} · {formatFileSize(imageFile?.size || 0)}</p>
          </div>
          <button type="button" className="chat-btn-icon" onClick={clearSelectedImage} title="Remove image">✕</button>
        </div>
      )}
      {mentionDraft && filteredMentions.length > 0 && (
        <div className="chat-mention-menu" role="listbox" aria-label="Mention suggestions">
          {filteredMentions.map((profile, index) => (
            <button
              key={profile.uid}
              type="button"
              className={"chat-mention-option" + (index === activeMentionIndex ? " active" : "")}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertMention(profile)}
            >
              <strong>{profile.displayName}</strong>
              <span>@{profile.mentionHandle}</span>
            </button>
          ))}
        </div>
      )}
      <div
        className={"chat-msg-input-inner" + (isDragActive ? " drag-active" : "")}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={onPickImage}
          disabled={composerDisabled || !!editingMessage}
        />
        {!editingMessage && (
          <button
            type="button"
            className="chat-btn-icon chat-attach-toggle"
            onClick={openFilePicker}
            title="Attach image"
            aria-label="Attach image"
            disabled={composerDisabled}
          >
            📷
          </button>
        )}
        <div className="chat-emoji-anchor">
          <button
            type="button"
            className="chat-btn-icon chat-emoji-toggle"
            onClick={() => setShowEmojiPicker((v) => !v)}
            title="Emoji"
            aria-label="Emoji"
            aria-expanded={showEmojiPicker}
          >
            ☺
          </button>
          <EmojiPickerPopover
            open={showEmojiPicker}
            onClose={() => setShowEmojiPicker(false)}
            onPick={addEmoji}
            className="chat-emoji-popover-composer"
          />
        </div>
        <textarea
          ref={taRef}
          rows={1}
          value={text}
          onChange={(e) => changeText(e.target.value, e.target.selectionStart)}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          onClick={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          onSelect={handleSelectionChange}
          placeholder={
            composerDisabled
              ? "Loading…"
              : editingMessage
                ? "Edit message"
                : placeholder || "Message — Enter to send, Shift+Enter for newline"
          }
          disabled={!!disabled}
          maxLength={MAX_BODY_LENGTH}
        />
        <button
          className="chat-send-btn"
          onClick={submit}
          disabled={!canSend}
          aria-label="Send message"
          title="Send message"
        >
          {isSubmitting ? "…" : editingMessage ? "✓" : "➤"}
        </button>
      </div>
      <div className="chat-drop-hint" aria-live="polite">
        {isDragActive
          ? "Drop an image here to attach it."
          : mentionDraft
          ? "Type to narrow the people list, then press Enter to mention them."
          : imageFile
            ? "Add a caption if you want, or send the image as-is."
            : "Type a message or attach an image, then press Enter to send."}
      </div>
    </div>
  );
}
